import React from "react";
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Divider,
  Row,
  Col,
  Typography,
} from "antd";
import dayjs from "dayjs";
import {
  StartThirdField,
  EndThirdField,
  StartTimeField,
  EndTimeField,
} from "./ThirdAndTimeFields";
import type { Third } from "../calendarCreateModal.helpers";

const { Text } = Typography;

interface BlockMaintenanceFieldsProps {
  kind: "block" | "maintenance";
  startThirdWatch: Third;
  endThirdWatch: Third;
  ingreso: any;
  range:
    | {
        propertyId: number;
        [k: string]: any;
      }
    | null
    | undefined;
  // 3-arg: el 3er argumento `exclude` (id+kind del evento en edición) hace
  // que los tercios ocupados por el PROPIO bloqueo (mismo id) no cuenten como
  // ocupados, pero CUALQUIER otro evento (otro bloqueo, arriendo, aseo) sí.
  isDayFullyOccupied?:
    | ((
        propertyId: number,
        dayISO: string,
        exclude?: { id?: number | string | null; kind?: string | null },
      ) => boolean)
    | null;
  // Evento en edición (para excluirlo del cómputo de ocupación). null en
  // modo creación. Round 2026-07-03.
  editExclude?: { id?: number | string | null; kind?: string | null } | null;
}

const BlockMaintenanceFields: React.FC<BlockMaintenanceFieldsProps> = ({
  kind,
  startThirdWatch,
  endThirdWatch,
  ingreso,
  range,
  isDayFullyOccupied,
  editExclude,
}) => {
  return (
    <>
      <Divider orientation="left">
        {kind === "block" ? "Bloqueo" : "Mantención"}
      </Divider>

      {/*
       * Admin puede modificar fecha, tercio y hora de entrada y
       * salida del bloqueo/mantención. Fecha + tercio + hora se
       * agrupan JUNTOS porque describen el horario del evento en
       * conjunto (no son tres conceptos separados — son una
       * descripción coherente del instante de inicio/fin).
       *
       * Defaults: entrada = hoy 15:00 (evening) / salida = mañana
       * 10:59 (morning) → 1 noche. Las horas están atadas al
       * tercio (ver disabledTime en StartTimeField/EndTimeField).
       */}
      <Text strong>Entrada</Text>
      <Row gutter={16} style={{ marginTop: 8 }}>
        <Col xs={24} md={8}>
          <Form.Item
            name="ingreso"
            label="Fecha"
            rules={[
              { required: true, message: "Fecha inicio es obligatoria" },
            ]}
          >
            <DatePicker
              format="DD-MM-YYYY"
              style={{ width: "100%" }}
              placeholder="Seleccionar fecha"
              disabledDate={(current) => {
                if (!current) return false;
                if (!range || !isDayFullyOccupied) return false;
                // Excluimos SOLO el bloqueo en edición (por id): sus tercios no
                // cuentan como ocupados, así se pueden reencuadrar sus fechas.
                // Cualquier OTRO evento (otro bloqueo, arriendo, aseo) sigue
                // deshabilitando el día — no se puede encimar.
                return isDayFullyOccupied(
                  range.propertyId,
                  current.format("YYYY-MM-DD"),
                  editExclude ?? undefined,
                );
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <StartThirdField />
        </Col>
        <Col xs={24} md={8}>
          <StartTimeField third={startThirdWatch} />
        </Col>
      </Row>

      <Text strong>Salida</Text>
      <Row gutter={16} style={{ marginTop: 8 }}>
        <Col xs={24} md={8}>
          <Form.Item
            name="salida"
            label="Fecha"
            rules={[{ required: true, message: "Fecha fin es obligatoria" }]}
          >
            <DatePicker
              format="DD-MM-YYYY"
              style={{ width: "100%" }}
              placeholder="Seleccionar fecha"
              disabledDate={(current) => {
                if (!current) return false;
                const i = ingreso;
                if (i) {
                  const iDay = dayjs.isDayjs(i) ? i : dayjs(i);
                  if (current.isBefore(iDay, "day")) return true;
                }
                if (range && isDayFullyOccupied) {
                  // Ídem Entrada: excluir SOLO el bloqueo en edición por id.
                  return isDayFullyOccupied(
                    range.propertyId,
                    current.format("YYYY-MM-DD"),
                    editExclude ?? undefined,
                  );
                }
                return false;
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <EndThirdField />
        </Col>
        <Col xs={24} md={8}>
          <EndTimeField third={endThirdWatch} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Form.Item name="noches" label="Cantidad de noches">
            <InputNumber style={{ width: "100%" }} readOnly />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={24}>
          <Form.Item
            name="razon"
            label="Razón (visible para todos)"
            tooltip="Texto visible para administradores, clientes y aseadores que puedan ver este bloqueo."
            rules={[{ required: true, message: "Ingrese la razón" }]}
          >
            <Input placeholder="Ej: Pintura, visita técnica, reparación..." />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={24}>
          <Form.Item
            name="notas"
            label="Nota interna (solo visible para Admin)"
            tooltip="Estas notas NUNCA se muestran al cliente ni al aseador. Úsalas para información sensible o coordinación interna."
          >
            <Input.TextArea
              rows={3}
              placeholder="Solo admin verá esta nota. Opcional."
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default BlockMaintenanceFields;
